/**
 * Registro unico de secciones del panel.
 *
 * Una sola lista alimenta las tres cosas que tienen que estar de acuerdo si o
 * si: las rutas que declara el router (`App.tsx`), los links del menu
 * (`AppLayout.tsx`) y el acotado por tienda de cada pantalla. Cuando eso vive
 * en tres lugares, tarde o temprano hay una ruta que existe y no esta en el
 * menu, o un link que lleva a un 404 propio.
 *
 * Como se agrega una seccion
 * --------------------------
 * 1. Si la pantalla ya existe en `src/screens/`, se importa y se pone en
 *    `element`.
 * 2. Si todavia no existe, se declara un `ScreenBlueprint` y se usa
 *    `screenComponent(...)`: la ruta queda viva mostrando "en construccion", y
 *    el dia que alguien cree `src/screens/<Module>.tsx` la pantalla aparece
 *    sola, **sin tocar este archivo** (ver `lazyScreen.tsx`).
 *
 * Ese "sin tocar este archivo" es el punto: las pantallas y el esqueleto los
 * escriben personas distintas y no queremos que se pisen.
 */

import type { ReactNode } from "react";

import { ProfitabilityScreen } from "../screens/ProfitabilityScreen";
import { StoreOverviewScreen } from "../screens/StoreOverviewScreen";
import { screenComponent } from "./lazyScreen";
import { StoreScoped } from "./StoreScope";
import type { ScreenBlueprint } from "./UnderConstruction";

export interface AppSection {
  /** Id estable, para keys de React. */
  readonly id: string;
  /** Path absoluto. `/` es la seccion inicial. */
  readonly path: string;
  /** Texto del menu. Corto: la barra lateral es angosta. */
  readonly label: string;
  /** Una linea de contexto; se muestra bajo el label y como `title`. */
  readonly hint: string;
  /**
   * `store`: la pantalla necesita una tienda seleccionada y se monta dentro de
   * `<StoreScoped>`. `global`: no la necesita (o los endpoints todavia no la
   * aceptan, que es el caso de Rentabilidad).
   */
  readonly scope: "store" | "global";
  readonly element: ReactNode;
}

/** Pantallas que otros van a escribir. Ver `lazyScreen.tsx`. */
const CATALOG: ScreenBlueprint = {
  module: "CatalogScreen",
  title: "Catalogo",
  purpose:
    "Listado de productos de la tienda seleccionada, con stock y estado. " +
    "Reemplaza la vista de productos del GUI de escritorio.",
  notes: [
    "Ya existe: src/screens/CatalogScreen.tsx reexporta la implementacion, " +
      "que vive en src/screens/catalog/ junto con la tabla, los filtros, el " +
      "paginador y el cliente de /api/v1/products/.",
  ],
};

const INVOICING: ScreenBlueprint = {
  module: "InvoicingScreen",
  title: "Facturacion",
  purpose:
    "Facturas AFIP de la tienda: emitidas, fallidas y pendientes, con el " +
    "detalle de cada una. Reemplaza la pestania 'Facturacion' del GUI.",
  notes: [
    "Verificar con `npm run gen:api` que exista un endpoint de listado de " +
      "facturas acotado por tienda. Si no existe, el trabajo es del backend: " +
      "no se inventa una lista en el front.",
  ],
};

const PUBLICATIONS: ScreenBlueprint = {
  module: "PublicationsScreen",
  title: "Publicacion",
  purpose:
    "Estado de las publicaciones en MercadoLibre y de las tareas de " +
    "publicacion masiva. Reemplaza el flujo de scripts/publication/.",
  notes: [
    "Antes de tocar nada de publicacion, leer docs/ML_PUBLICATION_CRITICAL_RULES.md.",
    "Las tareas corren en Celery: la pantalla observa estado, no lo inventa.",
  ],
};

const PRICING: ScreenBlueprint = {
  module: "PricingScreen",
  title: "Pricing",
  purpose:
    "Reglas de precio y recalculo sobre los productos de la tienda. " +
    "Apoyada en el PricingEngine de src/services/.",
  notes: [
    "Ojo: los endpoints de /pricing que existen hoy (top-profitable, " +
      "low-margin) NO reciben store_id y devuelven datos de todas las " +
      "tiendas. Si la pantalla se presenta como 'de la tienda', tiene que " +
      "decirlo, como ya hace Rentabilidad.",
  ],
};

/**
 * Krey: la unica de esta tanda que ya tiene pantalla escrita.
 *
 * Se declara igual que las demas (por `screenComponent`) y no con un import
 * estatico, para que siga siendo un chunk aparte. `src/screens/KreyScreen.tsx`
 * existe, asi que el cartel de "en construccion" no se ve nunca.
 */
const KREY: ScreenBlueprint = {
  module: "KreyScreen",
  title: "Krey",
  purpose:
    "Leer una ficha del proveedor krey.com.ar, revisar el payload calculado " +
    "para MercadoLibre y prepararlo. Reemplaza krey_publication_view.py.",
  notes: [
    "El paso de publicar todavia no tiene backend: /api/v1/publication/* " +
      "publica clonando un item que YA existe en ML (source_ml_id), y un " +
      "producto de Krey no existe ahi. La pantalla lo dice y deja el boton " +
      "apagado en vez de fingir.",
  ],
};


/**
 * Replicacion: publicar un item de una tienda en la otra, de a uno.
 *
 * No se confunde con Publicacion: aquella mira corridas y estado, esta es el
 * formulario de un solo producto que hoy es `replication_view.py`.
 */
const REPLICATION: ScreenBlueprint = {
  module: "ReplicationScreen",
  title: "Replicacion",
  purpose:
    "Replicar UNA publicacion de una tienda a la otra: ml_id de origen, " +
    "precio (markup o fijo), stock, cantidad y titulo propio. Reemplaza " +
    "replication_view.py.",
  notes: [
    "El endpoint es POST /api/v1/publication/jobs, el mismo que usa la " +
      "pantalla de Publicacion: PublicationItemRequest ya acepta title, " +
      "price, stock, markup, new_quantity, unit y free_shipping. No hace " +
      "falta un endpoint nuevo, y NO hay que duplicar el encolado.",
    "CUIDADO con la idempotencia: title, price, stock, new_quantity y unit " +
      "entran en build_idempotency_key. Mandar un valor distinto del default " +
      "crea una clave nueva, o sea un SEGUNDO item del mismo producto en la " +
      "cuenta real. Leer el comentario de src/screens/publication/api.ts.",
    "Esto crea publicaciones reales en MercadoLibre: accion explicita, " +
      "confirmacion con el alcance a la vista y guarda contra el doble click. " +
      "El patron ya esta resuelto en src/screens/publication/EnqueuePanel.tsx.",
  ],
};

/**
 * Monitor: que esta pasando y cuando fue la ultima sincronizacion.
 *
 * Reemplaza `monitor_view.py` y `tasks_view.py`, que leian
 * `publication_task_ids.json` del disco local y le preguntaban a
 * `celery_app.control.inspect()` -- dos cosas que no existen desde un navegador.
 */
const MONITOR: ScreenBlueprint = {
  module: "MonitorScreen",
  title: "Monitor",
  purpose:
    "Estado del sistema: conteos por tienda, rollup de publication_jobs y " +
    "estado de sincronizacion con MercadoLibre. Reemplaza monitor_view.py y " +
    "tasks_view.py.",
  notes: [
    "Endpoints reales: GET /api/v1/monitoring/summary y GET " +
      "/api/v1/monitoring/sync (los dos con store_id OPCIONAL: sin el " +
      "devuelven todas las tiendas concedidas), POST " +
      "/api/v1/monitoring/sync-runs y GET /api/v1/monitoring/sync-runs/{id}.",
    "La respuesta de /monitoring/sync trae run_history_available=false: NO " +
      "hay tabla de corridas, asi que 'fallo la ultima sync' no se puede " +
      "contestar. Si ese flag es false, no se dibuja el panel de ultima " +
      "corrida. El campo se llama last_product_change_at y no last_sync_at " +
      "porque un PATCH de baneo mueve el mismo timestamp.",
    "POST /monitoring/sync-runs responde 501 mientras no exista la task " +
      "ml.sync_store_products: si contesta 501, esconder el boton de " +
      "sincronizar en vez de dejarlo fallar.",
    "ApiClient solo tiene get(). Para el POST, copiar el postJson de " +
      "src/screens/publication/api.ts (reusa ApiError/NetworkError) o " +
      "agregarle post() al cliente. Y schema.gen.ts no lista /monitoring/*: " +
      "hay que regenerarlo o espejar los tipos a mano.",
  ],
};

/**
 * Prompts de IA. Es la unica seccion **global** de esta tanda: la tabla
 * `ai_prompts` no tiene `store_id`, asi que no hay nada que acotar por tienda.
 */
const PROMPTS: ScreenBlueprint = {
  module: "PromptsScreen",
  title: "Prompts",
  purpose:
    "Editor de los prompts de IA que usan las comparaciones y el pricing. " +
    "Reemplaza prompt_settings_view.py.",
  notes: [
    "Nueve endpoints reales bajo /api/v1/prompts/: listado, detalle, alta, " +
      "PATCH, DELETE, marcar default, catalogo de categorias con sus " +
      "variables, default por categoria y validacion de un template sin " +
      "guardarlo (POST /prompts/validate).",
    "Recurso GLOBAL: editar un prompt cambia como se analizan los productos " +
      "de TODAS las tiendas. Leer pide alguna tienda concedida; escribir pide " +
      "rol admin. La pantalla tiene que decirlo, porque el selector de tienda " +
      "de arriba no aplica aca.",
    "Reglas del backend que la pantalla tiene que respetar: 409 si se " +
      "intenta dejar como default un prompt inactivo; 422 ante un campo " +
      "enviado explicitamente en null en el PATCH (no se puede vaciar un " +
      "opcional por la API); y la categoria 'categorization' se guarda sin " +
      "validar (la respuesta trae checked:false, que NO es lo mismo que " +
      "valid:true).",
    "En produccion la tabla ai_prompts esta VACIA, aunque una base recien " +
      "migrada trae un prompt sembrado por la migracion 537097e143d6. La " +
      "pantalla tiene que verse bien con cero filas.",
  ],
};

const CatalogScreen = screenComponent(CATALOG);
const ReplicationScreen = screenComponent(REPLICATION);
const PublicationsScreen = screenComponent(PUBLICATIONS);
const KreyScreen = screenComponent(KREY);
const PricingScreen = screenComponent(PRICING);
const InvoicingScreen = screenComponent(INVOICING);
const MonitorScreen = screenComponent(MONITOR);
const PromptsScreen = screenComponent(PROMPTS);

/**
 * El orden es el del menu, y es el del trabajo real: primero se mira el estado
 * general y la tienda, despues el catalogo, despues lo que se publica (de la
 * otra tienda o del proveedor), despues los precios, despues lo que se factura,
 * y al final lo que se opera de vez en cuando (monitor y prompts).
 */
export const SECTIONS: readonly AppSection[] = [
  {
    id: "home",
    path: "/",
    label: "Inicio",
    hint: "Estado general del sistema",
    scope: "store",
    element: <StoreScoped>{(store) => <StoreOverviewScreen store={store} />}</StoreScoped>,
  },
  {
    id: "products",
    path: "/productos",
    label: "Catalogo",
    hint: "Productos de la tienda",
    scope: "store",
    element: <StoreScoped>{() => <CatalogScreen />}</StoreScoped>,
  },
  {
    id: "replication",
    path: "/replicacion",
    label: "Replicacion",
    hint: "Publicar un producto de una tienda en la otra",
    scope: "store",
    element: <StoreScoped>{() => <ReplicationScreen />}</StoreScoped>,
  },
  {
    id: "publications",
    path: "/publicaciones",
    label: "Publicacion",
    hint: "Publicaciones en MercadoLibre y tareas en curso",
    scope: "store",
    element: <StoreScoped>{() => <PublicationsScreen />}</StoreScoped>,
  },
  {
    id: "krey",
    path: "/krey",
    label: "Krey",
    hint: "Preparar una publicacion desde una ficha del proveedor",
    scope: "store",
    element: <StoreScoped>{() => <KreyScreen />}</StoreScoped>,
  },
  {
    id: "pricing",
    path: "/pricing",
    label: "Pricing",
    hint: "Reglas de precio y recalculo",
    scope: "store",
    element: <StoreScoped>{() => <PricingScreen />}</StoreScoped>,
  },
  {
    id: "profitability",
    path: "/rentabilidad",
    label: "Rentabilidad",
    hint: "Margenes por producto (hoy: de todas las tiendas)",
    scope: "global",
    element: <ProfitabilityScreen />,
  },
  {
    id: "invoicing",
    path: "/facturacion",
    label: "Facturacion",
    hint: "Facturas AFIP de la tienda",
    scope: "store",
    element: <StoreScoped>{() => <InvoicingScreen />}</StoreScoped>,
  },
  {
    id: "monitor",
    path: "/monitor",
    label: "Monitor",
    hint: "Conteos, trabajos en curso y estado de la sincronizacion",
    scope: "store",
    element: <StoreScoped>{() => <MonitorScreen />}</StoreScoped>,
  },
  {
    id: "prompts",
    path: "/prompts",
    label: "Prompts",
    hint: "Prompts de IA (configuracion global, no por tienda)",
    scope: "global",
    element: <PromptsScreen />,
  },
];
